/**
 * AuditPanel — Encart "Commander mon rapport d'audit préparatoire CONFILEX".
 *
 * S'affiche dans la page Workspace publique.
 *
 * États :
 *   - aucun audit → CTA "Commander l'audit" (89 € TTC)
 *   - audit initié (paiement en cours) → "Finaliser le paiement"
 *   - audit payé en cours de génération → spinner avec polling
 *   - audit prêt → "Consulter le rapport" + lien PDF
 *
 * Avant tout paiement : modale obligatoire affichant la clause de
 * non-responsabilité, avec une case à cocher de consentement.
 *
 * Strictement DOCUMENTAIRE. Aucune promesse de conseil juridique.
 */
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Sparkles, ShieldCheck, FileText, ExternalLink, Loader2, X, CheckCircle2,
  AlertTriangle,
} from "lucide-react";
import { apiClient } from "@/lib/api";
import { useAmbientScene } from "@/lib/useAmbientDucking";

const POLL_INTERVAL = 4000;
const POLL_MAX_ATTEMPTS = 30; // 2 min

function ConsentModal({ open, onClose, onConfirm, disclaimer, includes, excludes, loading, errorMsg,
                       waiverNotice, vatNotice }) {
  const [checked, setChecked] = useState(false);
  const [waiverChecked, setWaiverChecked] = useState(false);
  const [email, setEmail] = useState("");
  const [withExternal, setWithExternal] = useState(true);

  useEffect(() => {
    if (open) {
      setChecked(false);
      setWaiverChecked(false);
      setEmail("");
      setWithExternal(true);
    }
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-[#0B3A2A]/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
         data-testid="audit-consent-modal">
      <div className="bg-white w-full sm:max-w-xl rounded-t-3xl sm:rounded-2xl shadow-2xl max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-100 px-5 py-4 flex items-start justify-between gap-4 z-10">
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#C9A961]">CONFILEX · AUDIT PRÉPARATOIRE</p>
            <h2 className="font-serif italic text-xl text-[#0B3A2A] mt-1">
              Avant de commander votre rapport
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            className="p-1.5 text-slate-400 hover:text-slate-700"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4 text-sm text-slate-700">
          <div className="bg-[#FAF7F1] border border-[#C9A961]/30 rounded-xl px-4 py-3">
            <p className="text-[11px] uppercase tracking-[0.22em] text-[#8A6E3A] mb-2">
              Clause de non-responsabilité
            </p>
            <p className="text-[12px] leading-relaxed text-slate-700 italic">
              {disclaimer}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 mb-1.5">
                Inclus dans le rapport
              </p>
              <ul className="space-y-1">
                {includes?.map((it, i) => (
                  <li key={i} className="text-[12px] text-slate-700 flex items-start gap-1.5">
                    <CheckCircle2 className="w-3 h-3 text-emerald-600 mt-0.5 shrink-0" />
                    {it}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 mb-1.5">
                Non couvert
              </p>
              <ul className="space-y-1">
                {excludes?.map((it, i) => (
                  <li key={i} className="text-[12px] text-slate-500 flex items-start gap-1.5">
                    <AlertTriangle className="w-3 h-3 text-amber-500 mt-0.5 shrink-0" />
                    {it}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <label className="flex items-center gap-3 text-[12px] cursor-pointer">
            <input
              type="checkbox"
              checked={withExternal}
              onChange={(e) => setWithExternal(e.target.checked)}
              className="w-4 h-4 accent-[#0B3A2A]"
              data-testid="audit-include-external-checkbox"
            />
            <span className="text-slate-700">
              Inclure une recherche de jusqu'à <b>3 profils potentiellement adaptés</b> (hors plateforme, sources publiques) — incluse dans le forfait.
            </span>
          </label>

          <div>
            <label htmlFor="audit-email" className="block text-[11px] uppercase tracking-[0.22em] text-slate-500 mb-1">
              Email (optionnel — pour réception du reçu)
            </label>
            <input
              id="audit-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="prenom@exemple.fr"
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-[#C9A961]"
              data-testid="audit-email-input"
            />
          </div>

          <label className="flex items-start gap-3 cursor-pointer bg-[#0B3A2A]/5 border border-[#0B3A2A]/15 rounded-xl px-4 py-3">
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
              className="w-4 h-4 mt-0.5 accent-[#0B3A2A]"
              data-testid="audit-consent-checkbox"
            />
            <span className="text-[12px] text-slate-800 leading-relaxed">
              J'ai lu et j'accepte la clause de non-responsabilité ci-dessus. Je
              comprends que le rapport est un document préparatoire et qu'il ne
              constitue pas une consultation juridique, ni un conseil fiscal,
              financier ou comptable.
            </span>
          </label>

          <label className="flex items-start gap-3 cursor-pointer bg-[#FAF7F1] border border-[#C9A961]/40 rounded-xl px-4 py-3">
            <input
              type="checkbox"
              checked={waiverChecked}
              onChange={(e) => setWaiverChecked(e.target.checked)}
              className="w-4 h-4 mt-0.5 accent-[#C9A961]"
              data-testid="audit-waiver-checkbox"
            />
            <span className="text-[12px] text-slate-800 leading-relaxed">
              <b>Renonciation expresse au délai de rétractation de 14 jours.</b>{" "}
              {waiverNotice || (
                <>
                  Le rapport étant un contenu numérique délivré immédiatement
                  après paiement, je demande expressément sa génération avant
                  l'expiration du délai légal de rétractation et je renonce à
                  l'exercer (Art. L221-28 1° Code conso).
                </>
              )}
            </span>
          </label>

          {vatNotice && (
            <p className="text-[11px] text-slate-500 italic">
              {vatNotice}
            </p>
          )}

          {errorMsg && (
            <p className="text-[12px] text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {errorMsg}
            </p>
          )}
        </div>

        <div className="sticky bottom-0 bg-white border-t border-slate-100 px-5 py-4 flex flex-col sm:flex-row gap-2 items-center justify-between">
          <p className="text-xs text-slate-500 italic">
            Paiement sécurisé — CB, Apple Pay, Google Pay.
          </p>
          <button
            type="button"
            disabled={!checked || !waiverChecked || loading}
            onClick={() => onConfirm({ email, withExternal })}
            data-testid="audit-confirm-pay-btn"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-[#0B3A2A] text-white text-sm font-medium hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed transition shadow-md"
          >
            {loading
              ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirection…</>
              : <>Payer 89,00 € TTC <ExternalLink className="w-3.5 h-3.5" /></>}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AuditPanel({ accessToken, workspace }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [disclaimer, setDisclaimer] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [audit, setAudit] = useState(null);
  const [polling, setPolling] = useState(false);

  // Scène UX : "processing" pendant la génération du rapport
  useAmbientScene(
    audit && audit.payment_status === "paid" && !audit.report ? "processing" : null
  );

  // Récupère la clause d'avertissement au montage
  useEffect(() => {
    apiClient.get(`/workspace/${accessToken}/audit/disclaimer`)
      .then((r) => setDisclaimer(r.data))
      .catch(() => null);
  }, [accessToken]);

  // Si l'URL contient ?audit=... (retour Stripe success), on charge l'audit
  const auditQueryId = searchParams.get("audit");
  useEffect(() => {
    if (!auditQueryId) return;
    let cancelled = false;
    let attempt = 0;
    const tick = async () => {
      attempt += 1;
      try {
        const r = await apiClient.get(`/workspace/${accessToken}/audit/${auditQueryId}`);
        if (cancelled) return;
        setAudit(r.data);
        const ready = r.data?.payment_status === "paid" && r.data?.report;
        if (ready) {
          setPolling(false);
          return;
        }
        if (attempt < POLL_MAX_ATTEMPTS) {
          setPolling(true);
          setTimeout(tick, POLL_INTERVAL);
        } else {
          setPolling(false);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e?.response?.data?.detail || "Impossible de charger l'audit.");
          setPolling(false);
        }
      }
    };
    tick();
    return () => { cancelled = true; };
  }, [accessToken, auditQueryId]);

  const startCheckout = async ({ email, withExternal }) => {
    setLoading(true); setError(null);
    try {
      const origin = window.location.origin;
      const r = await apiClient.post(`/workspace/${accessToken}/audit/checkout`, {
        origin_url: origin,
        consent_accepted: true,
        waiver_14_days_accepted: true,
        include_external_search: withExternal,
        customer_email: email || null,
      });
      window.location.href = r.data.url;
    } catch (e) {
      setError(e?.response?.data?.detail || "Erreur lors de la création du paiement.");
      setLoading(false);
    }
  };

  const dismissAudit = () => {
    setAudit(null);
    setSearchParams({});
  };

  // ----- RENDER -----

  // Audit prêt
  if (audit && audit.payment_status === "paid" && audit.report) {
    const m = audit.report?.maturity;
    return (
      <section className="rounded-2xl border border-[#C9A961]/40 bg-gradient-to-br from-[#FAF7F1] to-white p-5 shadow-sm"
               data-testid="audit-ready-panel">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#8A6E3A]">RAPPORT D'AUDIT · CONFILEX</p>
            <h3 className="font-serif italic text-lg text-[#0B3A2A] mt-1">
              Votre rapport préparatoire est prêt
            </h3>
          </div>
          <button onClick={dismissAudit} className="text-slate-400 hover:text-slate-600 p-1" aria-label="Fermer">
            <X className="w-4 h-4" />
          </button>
        </div>

        {m && (
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#0B3A2A] text-white text-[11px] mb-3"
               data-testid="audit-maturity-badge">
            <ShieldCheck className="w-3 h-3 text-[#C9A961]" />
            {m.label}
          </div>
        )}

        {audit.report?.summary && (
          <p className="text-[13px] text-slate-700 leading-relaxed italic mb-3 line-clamp-4">
            {audit.report.summary}
          </p>
        )}

        <div className="flex flex-wrap gap-2">
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/workspace/${accessToken}/audit/${audit.id}/report.pdf`}
            target="_blank" rel="noreferrer"
            data-testid="audit-open-pdf-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#0B3A2A] text-white text-[12px] font-medium hover:brightness-110 transition shadow"
          >
            <FileText className="w-3.5 h-3.5 text-[#C9A961]" />
            Ouvrir le rapport PDF
          </a>
          <span className="text-[11px] text-slate-500 self-center italic">
            Document préparatoire — ne constitue pas une consultation.
          </span>
        </div>
      </section>
    );
  }

  // Audit en cours de génération
  if (audit && audit.payment_status === "paid" && !audit.report) {
    return (
      <section className="rounded-2xl border border-[#C9A961]/40 bg-[#FAF7F1] p-5 text-center"
               data-testid="audit-generating-panel">
        <Loader2 className="w-6 h-6 text-[#C9A961] animate-spin mx-auto mb-2" />
        <p className="text-sm text-[#0B3A2A] font-medium">
          Votre rapport est en cours de génération
        </p>
        <p className="text-[12px] text-slate-500 mt-1 italic">
          Cela prend généralement moins d'une minute. Vous pouvez fermer la page :
          {workspace?.first_name ? ` nous reviendrons vers vous, ${workspace.first_name}.` : " votre rapport sera conservé dans votre dossier."}
        </p>
      </section>
    );
  }

  // Audit initié mais paiement non finalisé (rare — l'utilisateur a annulé Stripe)
  if (audit && audit.payment_status !== "paid") {
    return (
      <section className="rounded-2xl border border-amber-200 bg-amber-50 p-5"
               data-testid="audit-pending-payment-panel">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-amber-900 font-medium">
              Paiement non finalisé
            </p>
            <p className="text-[12px] text-amber-800 mt-1">
              Votre commande a été initiée mais le paiement n'a pas été confirmé.
              Vous pouvez la relancer ci-dessous.
            </p>
            <button
              onClick={() => setModalOpen(true)}
              className="mt-3 text-[12px] text-amber-900 underline"
            >
              Recommencer la commande
            </button>
          </div>
        </div>
        <ConsentModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          onConfirm={startCheckout}
          disclaimer={disclaimer?.disclaimer}
          includes={disclaimer?.includes}
          excludes={disclaimer?.excludes}
          waiverNotice={disclaimer?.waiver_14_days_notice}
          vatNotice={disclaimer?.vat_notice}
          loading={loading}
          errorMsg={error}
        />
      </section>
    );
  }

  // Pas d'audit : CTA principal
  return (
    <section className="rounded-2xl bg-gradient-to-br from-[#0B3A2A] to-[#0B3A2A] text-white p-5 shadow-lg"
             data-testid="audit-cta-panel">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-[#C9A961]/20 flex items-center justify-center shrink-0">
          <Sparkles className="w-5 h-5 text-[#C9A961]" />
        </div>
        <div className="flex-1">
          <p className="text-[10px] uppercase tracking-[0.28em] text-[#C9A961]">RAPPORT D'AUDIT PRÉPARATOIRE</p>
          <h3 className="font-serif italic text-lg mt-1 leading-tight">
            Un dossier prêt à présenter à un professionnel
          </h3>
          <p className="text-[12px] text-white/75 mt-2 leading-relaxed">
            Synthèse documentaire structurée, chronologie consolidée, identification
            des problématiques potentielles et profils potentiellement adaptés —
            réunis dans un PDF institutionnel.
          </p>
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 mt-3">
            <span className="font-serif italic text-2xl text-white">89,00&nbsp;€</span>
            <span className="text-[10px] uppercase tracking-[0.22em] text-white/60">TTC · sans engagement</span>
          </div>
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            data-testid="audit-open-modal-btn"
            className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white text-[#0B3A2A] text-[12px] font-medium hover:bg-[#FAF7F1] transition shadow"
          >
            Commander mon rapport
            <ExternalLink className="w-3 h-3" />
          </button>
          <p className="text-[10px] text-white/55 italic mt-3 leading-relaxed">
            Ce rapport est un document préparatoire — il ne constitue pas une
            consultation juridique, fiscale, comptable ou financière.
          </p>
        </div>
      </div>

      <ConsentModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onConfirm={startCheckout}
        disclaimer={disclaimer?.disclaimer}
        includes={disclaimer?.includes}
        excludes={disclaimer?.excludes}
        waiverNotice={disclaimer?.waiver_14_days_notice}
        vatNotice={disclaimer?.vat_notice}
        loading={loading}
        errorMsg={error}
      />
    </section>
  );
}
